/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {Injectable} from '@angular/core';
import {AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup, ValidatorFn} from '@angular/forms';

import {TranslateService} from '@ngx-translate/core';

import {Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';

import * as moment from 'moment/moment';

import * as IBAN from 'iban';
import {DATE_FORMAT, DATE_MAX, DATE_MIN} from '../../app.constants';
import {ReportDefinition} from '../../models/models.interfaces';
import {EmployeesService} from '../employees/employees.service';
import {DataTransformer} from '../form-generator/data-transformer/data-transformer';
import { AnyData } from '../api';

const NON_ALPHANUM = /[^a-zA-Z0-9]/g;
const LOCALIZED_SPACE_SEPARATOR = /[\s\u00A0\u202F]/g;
const EMAIL_EXP = new RegExp(['^(([^<>()[\\]\\\.,;:\\s@\"]+(\\.[^<>()\\[\\]\\\.,;:\\s@\"]+)*)',
  '|(".+"))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.',
  '[0-9]{1,3}\])|(([a-zA-Z\\-0-9]+\\.)+',
  '[a-zA-Z]{2,}))$'].join(''));

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function getGroupingPattern(separator: string): string {
  return separator === ' ' ? '[\\s\\u00A0\\u202F]' : escapeRegex(separator);
}


@Injectable()
export class Validators {

  constructor(private translate: TranslateService, private employeesService: EmployeesService) {
  }

  public static validateISO8601DateOrNull(control: UntypedFormControl) {
    if (!control || control.value === undefined || control.value === '') {
      return;
    }

    if (control.value === null) {
      return null;
    }

    return Validators.validateISO8601Date(control);
  }

  /**
   * Validate a date with ISO 8601 format, default format is YYYY-MM-DD and stricter format check
   * @param control
   * @param dateFormat
   * @param stricterFormatCheck
   * @returns {any}
   */
  public static validateISO8601Date(control: UntypedFormControl, dateFormat: string = DATE_FORMAT, stricterFormatCheck = true) {
    if (!control || control.value === undefined || control.value === '') {
      return;
    }

    if (!moment(control.value, dateFormat, stricterFormatCheck).isValid()) {
      return {'invalidISO8601Date': true};
    }

    return null;
  }

  /**
   * Passwords matching validator
   * @param newPassword
   * @param confirmPassword
   * @returns {any}
   */
  public static matchingPasswordsValidator(newPassword: UntypedFormControl, confirmPassword: UntypedFormControl): any {
    if (confirmPassword.value === '') {
      return {'required': true};
    } else if (newPassword.value !== confirmPassword.value) {
      return {'mismatchedPasswords': true};
    }
    return null;
  }

  /**
   * IBAN validator
   * @param control
   * @returns {any}
   *
   **/
  public static IBANValidator(control) {
    if (!control || !control.value) {
      return null;
    }

    const formattedValue: string = control.value.replace(NON_ALPHANUM, '').toUpperCase();
    if (control.value !== formattedValue) {
      control.setValue(formattedValue);
      control.updateValueAndValidity();
    }

    return IBAN.isValid(control.value) ? null : {'invalidIBAN': true};
  }

  /**
   * BIC validator
   * @param control
   * @returns {any}
   *
   **/
  public static BICValidator(control) {
    const bicRegex = /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/;

    if (control && control.value && (!bicRegex.test(control.value) || (control.value.slice(4, 6) !== 'GP' && !IBAN.countries[control.value.slice(4, 6)]))) {
      return {'invalidBIC': true};
    }

    return null;
  }

  /**
   * BSB validator
   * @param control
   * @returns {any}
   *
   **/
  public static BSBValidator(control) {
    const bsbRegex = /^[0-9]{3}-?[0-9]{3}$/;

    if (control && control.value && !bsbRegex.test(control.value)) {
      return {'invalidBSB': true};
    }

    return null;
  }

  public static passwordValidator(minLength: number,
                                  numUpperCase: number,
                                  numLowerCase: number,
                                  numDigit: number,
                                  numSpecial: number): any {
    return (control: AbstractControl): any => {

      const value: string = control.value || '';

      const upperFound: RegExpMatchArray = value.match(/[A-Z]/g);
      const lowerFound: RegExpMatchArray = value.match(/[a-z]/g);
      const digitFound: RegExpMatchArray = value.match(/[\d]/g);
      const specialFound: RegExpMatchArray = value.match(/[!"#$%&\()*\+,-./:\[\]\\;<=>?@^_`{|}~]/g);
      const invalidFound: RegExpMatchArray = value.match(/[№]/g);

      if (invalidFound && invalidFound.length > 0) {
        return {
          invalid: true
        };
      }

      if (isError(lowerFound, numLowerCase)) {
        return buildErrorObject('minLowerCase', numLowerCase);
      }

      if (isError(upperFound, numUpperCase)) {
        return buildErrorObject('minUpperCase', numUpperCase);
      }

      if (isError(specialFound, numSpecial)) {
        return buildErrorObject('minSpecial', numSpecial);
      }

      if (isError(digitFound, numDigit)) {
        return buildErrorObject('minDigit', numDigit);
      }

      if (value.length < minLength) {
        return buildErrorObject('minLength', minLength);
      }

      return null;
    };


    function buildErrorObject(name: string, value: number) {
      return {
        [name]: {
          invalid: true,
          minValue: value
        }
      };
    }

    function isError(foundResult: RegExpMatchArray, numCharacters: number): boolean {
      if (!numCharacters) {
        return false;
      }
      return !foundResult || foundResult.length < numCharacters;
    }
  }

  public static mismatchedPasswordsValidator(newPassword: string, confirmPassword: string): any {
    return (form: UntypedFormGroup): any => {
      const controlPassword: AbstractControl = form.controls[newPassword];
      const controlConfirm: AbstractControl = form.controls[confirmPassword];

      if (controlPassword.value !== controlConfirm.value) {
        return {
          mismatchedPasswords: true
        };
      }

      return null;
    };
  }

  public static valueInListValidator(list: any[]) {
    return (control: UntypedFormControl) => {
      if (control && control.value && list && !list.some((item) => item.value === control.value)) {
        return {'valueNotInList': true};
      }
      return null;
    };
  }

  public static displayTextInListValidator(list: any[]) {
    return (control: UntypedFormControl) => {
      if (control && control.value && list && !list.some((item) => item.display === control.value)) {
        return {'displayTextNotInList': true};
      }
      return null;
    };
  }

  public static noEmptyArray(control: UntypedFormControl) {
    let notEmpty: boolean = false;

    if (!control || !control.value || !Array.isArray(control.value)) {
      return null;
    }

    if (control.value.length) {
      for (let i = 0; i <= control.value.length; i++) {
        if (control.value[i] !== undefined) {
          notEmpty = true;
        }
      }
    }

    return notEmpty ? null : {'noEmptyArray': true};
  }

  public static minDateValidator(minDate: string = DATE_MIN) {
    return (control: UntypedFormControl) => {
      if (!control || !control.value) {
        return null;
      }

      if (moment(control.value).isBefore(minDate)) {
        return {'minDateError': true};
      }

      return null;
    };
  }

  public static maxDateValidator(maxDate: string = DATE_MAX) {
    return (control: UntypedFormControl) => {
      if (!control || !control.value) {
        return null;
      }

      if (moment(control.value).isAfter(maxDate)) {
        return {'maxDateError': true};
      }

      return null;
    };
  }

  public static regExpValidator(regexp: string, mask?: string) {
    return (control: UntypedFormControl) => {
      if (!control || !control.value) {
        return null;
      }

      const value: string = mask ? DataTransformer.extractMask(control.value, mask) : control.value;
      const match = new RegExp(regexp)[Symbol.match](value);

      if ( !match || match.input.length !== match[0].length) {
        return {'regexp': true};
      }

      return null;
    };
  }

  public static isString(value: any) {
    return typeof value === 'string' || value instanceof String;
  }

  public static emailValidator(control: UntypedFormControl) {
    if (!control || !control.value || (Validators.isString(control.value) && control.value.match(EMAIL_EXP))) {
      return null;
    } else {
      return {'invalidEmailAddress': true};
    }
  }

  public static noWhitespaceValidator(control: UntypedFormControl) {
    const isValid = !((control.value || '').trim().length === 0);
    return isValid ? null : {'whitespace': true};
  }

  public static reportDefinitionNameValidator(fileContentType: string, uploadableReports: ReportDefinition[]) {
    return (control: UntypedFormControl) => {
      if (!control || !control.value || !fileContentType) {
        return null;
      }
      const reportContentType = uploadableReports.find(report => report.reportName === control.value).contentType;

      if (fileContentType !== reportContentType) {
        return {'invalidContentType': true};
      }

      return null;
    };
  }

  public static SSNFranceValidator(control: UntypedFormControl) {
    if (!control || !control.value) {
      return null;
    }
    const value: string = control.value;
    if (value.length !== 15) {
      return {invalidSSNFrance: true};
    }

    try {
      const gender: string = value.slice(0, 1);
      const year: string = value.slice(1, 3);
      const month: string = value.slice(3, 5);
      let department: string = value.slice(5, 7);
      const city: string = value.slice(7, 10);
      const certificate: string = value.slice(10, 13);
      const key: string = value.slice(13, 15);

      department = department
        .replace('2A', '19')
        .replace('2B', '18');

      const result = 97 - parseInt(gender + year + month + department + city + certificate, 10) % 97;

      if (result !== parseInt(key, 10)) {
        return {invalidSSNFrance: true};
      }
    } catch (exception) {
      return {invalidSSNFrance: true};
    }
    return null;
  }

  public isEmailExistValidator(c: UntypedFormControl, empId: number): Observable<unknown> {
    if (!c.value) {
      return of(null);
    }

    return this.employeesService.checkEmailAvailable(c.value, empId)
      .pipe(
        map((status) => {
          if (!status) {
            return {emailExists: true};
          }
          return null;
        })
      );
  }

  /**
   * Handles custom validation messages
   * @param validatorName
   * @param validatorValue
   * @returns {any}
   */
  public getValidatorErrorMessage(validatorName: string, validatorValue?: any) {
    const config = {
      'required': this.translate.instant('FORMS.VALIDATORS.required'),
      'minlength': this.translate.instant('FORMS.VALIDATORS.minlength', {length: validatorValue.requiredLength}),
      'maxlength': this.translate.instant('FORMS.VALIDATORS.maxlength', {length: validatorValue.requiredLength}),
      'mismatchedPasswords': this.translate.instant('FORMS.VALIDATORS.mismatchedPasswords'),
      'invalidISO8601Date': this.translate.instant('FORMS.VALIDATORS.invalidISO8601Date'),
      'invalidIBAN': this.translate.instant('FORMS.VALIDATORS.invalidIBAN'),
      'minLength': this.translate.instant('FORMS.VALIDATORS.minlength', {length: validatorValue.minValue}),
      'minSpecial': this.translate.instant('FORMS.VALIDATORS.minSpecial', {length: validatorValue.minValue}),
      'minUpperCase': this.translate.instant('FORMS.VALIDATORS.minUpper', {length: validatorValue.minValue}),
      'minLowerCase': this.translate.instant('FORMS.VALIDATORS.minLower', {length: validatorValue.minValue}),
      'minDigit': this.translate.instant('FORMS.VALIDATORS.minDigit', {length: validatorValue.minValue}),
      'mismatched': this.translate.instant('FORMS.VALIDATORS.mismatched'),
      'valueNotInList': this.translate.instant('FORMS.VALIDATORS.valueNotInList'),
      'emailExists': this.translate.instant('FORMS.VALIDATORS.email_exists_for_employee_within_company')
      /*
       'invalidEmailAddress': 'Invalid email address',
       'invalidPassword': 'Invalid password. Password must be at least 6 characters long, and contain a number.'
       */
    };

    return config[validatorName];
  }


  /**
   * If you need at least X checkbox(es) to be checked
   * @param minRequired
   * @returns {ValidatorFn}
   */
  public requireCheckboxesToBeCheckedValidator(minRequired = 1): ValidatorFn {
    return function validate(formGroup: UntypedFormGroup | UntypedFormArray) {
      let checked = 0;

      Object.keys(formGroup.controls).forEach(key => {
        const control = formGroup.controls[key];

        if (control.value === true) {
          checked++;
        }
      });

      if (checked < minRequired) {
        return {
          requireCheckboxesToBeChecked: true
        };
      }

      return null;
    };
  }

  public static validateBeginDate(beginDate: string, endDate: string, contractBegin: string, contractEnd: string) {
    const warnings: string[] = [];
    if (beginDate && !moment(beginDate, DATE_FORMAT, true).isValid()) warnings.push('invalid_format');
    if (beginDate && endDate && moment(beginDate).isAfter(moment(endDate))) warnings.push('begin_after_end');
    if (beginDate && contractBegin && moment(beginDate).isBefore(moment(contractBegin))) warnings.push('begin_before_contract');
    if (beginDate && contractEnd && moment(beginDate).isAfter(moment(contractEnd))) warnings.push('begin_after_contract');
    if (!beginDate) warnings.push('incomplete');
    return warnings;
  }

  public static validateEndDate(beginDate: string, endDate: string, contractBegin: string, contractEnd: string) {
    const warnings: string[] = [];
    if (endDate && !moment(endDate, DATE_FORMAT, true).isValid()) warnings.push('invalid_format');
    if (beginDate && endDate && moment(beginDate).isAfter(moment(endDate))) warnings.push('end_before_begin');
    if (endDate && contractBegin && moment(endDate).isBefore(moment(contractBegin))) warnings.push('end_outside_contract');
    if (endDate && contractEnd && moment(endDate).isAfter(moment(contractEnd))) warnings.push('end_outside_contract');
    if (!endDate) warnings.push('incomplete');
    return warnings;
  }

  public static parseLocalizedAmount(amount: AnyData, separator = ',', decimal = '.'): number | null {
    if (amount === undefined || amount === null || amount === '') {
      return null;
    }

    if (typeof amount === 'number') {
      return Number.isNaN(amount) ? null : amount;
    }

    const localized = String(amount).trim();
    const integerPattern = `(?:\\d{1,3}(?:${getGroupingPattern(separator)}\\d{3})+|\\d+)`;
    const decimalPattern = `(?:${escapeRegex(decimal)}\\d+)?`;
    const amountPattern = new RegExp(`^-?${integerPattern}${decimalPattern}$`);

    if (!amountPattern.test(localized)) {
      return null;
    }

    let normalized = separator === ' ' ? localized.replace(LOCALIZED_SPACE_SEPARATOR, '') : localized.split(separator).join('');
    normalized = normalized.split(decimal).join('.');

    const parsed = Number(normalized);
    return Number.isNaN(parsed) ? null : parsed;
  }

  public static validateAmount(amount: AnyData, separator = ',', decimal = '.') {
    const warnings: string[] = [];

    if (amount === undefined || amount === null || amount === '') {
      warnings.push('incomplete');
      return warnings;
    }

    if (this.parseLocalizedAmount(amount, separator, decimal) === null) {
      warnings.push('invalid_format');
    }

    return warnings;
  }
}
